import { auth } from '@/lib/firebase'
import { getIdTokenForUid } from '@/utils/auth'

export const BILLING_REQUEST_TIMEOUT_MS = 15_000

export function assertBillingOwner(uid: string) {
  if (auth.currentUser?.uid !== uid) {
    throw new Error('Signed-in account changed. Please try again with the original account.')
  }
}

/** Bound token acquisition as well as HTTP, and ignore late results after leaving the screen. */
export async function billingRequest<T>(
  uid: string,
  request: (token: string, signal: AbortSignal) => Promise<T>,
  parent?: AbortSignal
): Promise<T> {
  const controller = new AbortController()
  const abort = () => controller.abort(parent?.reason)
  parent?.addEventListener('abort', abort, { once: true })
  if (parent?.aborted) abort()
  const timer = setTimeout(
    () => controller.abort(new Error('The billing request timed out. Please try again.')),
    BILLING_REQUEST_TIMEOUT_MS
  )
  let rejectAbort: () => void = () => {}
  try {
    controller.signal.throwIfAborted()
    const aborted = new Promise<never>((_, reject) => {
      rejectAbort = () => reject(controller.signal.reason)
      controller.signal.addEventListener('abort', rejectAbort, { once: true })
    })
    const work = async () => {
      const token = await getIdTokenForUid(uid)
      controller.signal.throwIfAborted()
      assertBillingOwner(uid)
      const result = await request(token, controller.signal)
      controller.signal.throwIfAborted()
      assertBillingOwner(uid)
      return result
    }
    return await Promise.race([work(), aborted])
  } finally {
    clearTimeout(timer)
    parent?.removeEventListener('abort', abort)
    controller.signal.removeEventListener('abort', rejectAbort)
  }
}
